﻿import { ELI_TOKENS, EliType } from '../../../../../services/external-link-interface/EliService';
import { DragOutline, EditOutline, FilterOutline, LinkOutline, PlusOutline, TrashOutline } from '../../../../../icons/icons';
import { Button } from '@progress/kendo-react-buttons';
import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { EliCustomButton } from '../../../../../types/settings';
import { SvgIcon } from '@progress/kendo-react-common';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { setSetting } from '../../../../state/slices/settings-slice';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, InputPrefix, TextBox, TextBoxHandle } from '@progress/kendo-react-inputs';
import { useFormCheckboxValue, useFormStringValue } from '../../../home/hooks/forms';
import { Reveal } from '@progress/kendo-react-animation';

export const MAX_ELI_BUTTONS = 20;
export function SettingsEliGroup(props: { eliType: EliType }) {
    const eliButtons = useAppSelector((x) => {
        switch (props.eliType) {
            case EliType.airplane:
                return x.settings.eliAirplaneButtons;
            case EliType.airport:
                return x.settings.eliAirportButtons;
            case EliType.topBar:
                return x.settings.eliTopBarButtons;
        }
    });
    const dispatch = useAppDispatch();
    const [showNewDialog, setShowNewDialog] = React.useState<boolean>(false);
    const [showConfirmDelete, setShowConfirmDelete] = React.useState<number | null>(null);
    const [editItem, setEditItem] = React.useState<EliCustomButton | null>(null);
    const urlTextBoxRef = React.useRef<TextBoxHandle>(null);

    const sensors = useSensors(useSensor(PointerSensor));
    const [buttonIndexes, setButtonIndexes] = React.useState(eliButtons.map((_, index) => index));

    const newButtonName = useFormStringValue('');
    const newUrlTemplate = useFormStringValue('');
    const newFilter = useFormStringValue('');
    const newFilterSectionOpen = useFormCheckboxValue(false);
    const isNameDuplicate = React.useMemo(() => {
        const nameToCheck = newButtonName.value.trim().toLowerCase();
        if (editItem && editItem.label.trim().toLowerCase() === nameToCheck) {
            return false;
        }
        return eliButtons.some((b) => b.label.trim().toLowerCase() === nameToCheck);
    }, [newButtonName.value, eliButtons, editItem]);

    const settingKey = React.useMemo(() => {
        switch (props.eliType) {
            case EliType.airplane:
                return 'eliAirplaneButtons';
            case EliType.airport:
                return 'eliAirportButtons';
            case EliType.topBar:
                return 'eliTopBarButtons';
        }
    }, [props.eliType]);

    useEffect(() => {
        setButtonIndexes(eliButtons.map((_, index) => index));
    }, [eliButtons]);

    useEffect(() => {
        if (!showNewDialog && editItem === null) {
            newButtonName.onChange({ value: '' } as any);
            newUrlTemplate.onChange({ value: '' } as any);
            newFilter.onChange({ value: '' } as any);
            newFilterSectionOpen.onChange({ value: false } as any);
        } else if (editItem) {
            newButtonName.onChange({ value: editItem.label } as any);
            newUrlTemplate.onChange({ value: editItem.urlTemplate } as any);
            if (editItem.filter !== null) {
                newFilter.onChange({ value: editItem.filter } as any);
                newFilterSectionOpen.onChange({ value: true } as any);
            } else {
                newFilter.onChange({ value: '' } as any);
                newFilterSectionOpen.onChange({ value: false } as any);
            }
        }
    }, [showNewDialog, editItem]);

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                setButtonIndexes((items) => {
                    const oldIndex = items.indexOf(active.id as number);
                    const newIndex = items.indexOf(over.id as number);
                    return arrayMove(items, oldIndex, newIndex);
                });
                const movedButtons = arrayMove(eliButtons, active.id as number, over.id as number).filter(
                    (b) => b !== undefined && b !== null
                );
                dispatch(setSetting(settingKey, movedButtons));
            }
        },
        [dispatch, eliButtons]
    );

    const isUrlValid = (url: string) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    return (
        <div className={'d-flex flex-column m-1 flex-shrink-0 p-2'}>
            <div className={'d-flex justify-content-between align-items-center'}>
                <div className={'d-flex align-items-center gap-2'}>
                    <div>Custom buttons</div>
                    <div className={'text-secondary small'}>
                        {eliButtons.length} of {MAX_ELI_BUTTONS}
                    </div>
                </div>
                <Button
                    size={'small'}
                    themeColor={'primary'}
                    svgIcon={PlusOutline}
                    title={'Add new item'}
                    disabled={eliButtons.length >= MAX_ELI_BUTTONS}
                    onClick={() => setShowNewDialog(true)}></Button>
            </div>
            <div className={'d-flex flex-column gap-2 mt-2'}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToParentElement]}>
                    <SortableContext items={buttonIndexes} strategy={verticalListSortingStrategy}>
                        {eliButtons.map((button, index) => (
                            <EliButton
                                id={index}
                                button={button}
                                type={props.eliType}
                                onEdit={(btn) => {
                                    setEditItem(btn);
                                }}
                                onDelete={(idx) => setShowConfirmDelete(idx)}
                            />
                        ))}
                        {eliButtons.length === 0 && <div className={'secondary-text'}>No external links added</div>}
                    </SortableContext>
                </DndContext>
            </div>
            {(showNewDialog || editItem !== null) && (
                <Dialog
                    title={editItem ? 'Edit Custom Button' : 'Add Custom Button'}
                    onClose={() => {
                        setShowNewDialog(false);
                        setEditItem(null);
                    }}>
                    <div className={'d-flex flex-column gap-3'} style={{ minWidth: '470px' }}>
                        <div className={'d-flex flex-column flex-grow-1'}>
                            <div>Button Name</div>
                            <TextBox placeholder="e.g. View Profile" {...newButtonName} valid={!isNameDuplicate}></TextBox>
                            {isNameDuplicate && (
                                <span className={'text-danger small mt-1'} style={{ fontSize: '.75em' }}>
                                    A button with this name already exists
                                </span>
                            )}
                        </div>
                        <div className={'d-flex flex-column'} style={{ minWidth: '212px' }}>
                            <div>Url Template</div>
                            <TextBox
                                ref={urlTextBoxRef}
                                prefix={() => (
                                    <InputPrefix>
                                        <SvgIcon icon={LinkOutline} />
                                    </InputPrefix>
                                )}
                                placeholder="https://example.com?user=#USERID#"
                                {...newUrlTemplate}></TextBox>
                            {!isUrlValid(newUrlTemplate.value) && newUrlTemplate.value.trim() !== '' && (
                                <span className={'text-warning small mt-1'} style={{ fontSize: '.75em' }}>
                                    This URL may be invalid
                                </span>
                            )}
                        </div>
                        <div className={'d-flex flex-column'}>
                            <div>Available wildcards</div>
                            <div className={'d-flex flex-wrap gap-1'}>
                                {ELI_TOKENS[props.eliType].map((token) => (
                                    <span
                                        className={'fe-badge-outline clickable'}
                                        onClick={() => {
                                            if (!urlTextBoxRef || !urlTextBoxRef.current) {
                                                return;
                                            }
                                            const cursorPos = (urlTextBoxRef.current.element as HTMLInputElement).selectionStart || 0;
                                            const newValue =
                                                newUrlTemplate.value.slice(0, cursorPos) +
                                                `#${token}#` +
                                                newUrlTemplate.value.slice(cursorPos);
                                            newUrlTemplate.onChange({ value: newValue } as any);
                                        }}>
                                        #{token}#
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div>
                            {props.eliType !== EliType.topBar && (
                                <div
                                    className={'d-flex align-items-center gap-1 cursor-pointer'}
                                    onClick={() => {
                                        newFilterSectionOpen.onChange({ value: !newFilterSectionOpen.value } as any);
                                    }}>
                                    <Checkbox {...newFilterSectionOpen}></Checkbox>
                                    <SvgIcon icon={FilterOutline}></SvgIcon>
                                    Include filter
                                </div>
                            )}
                            <Reveal className={'w-100'}>
                                {newFilterSectionOpen.value && (
                                    <div className={'mt-2'}>
                                        <TextBox prefix={<SvgIcon icon={LinkOutline} />} placeholder="e.g. JBU*" {...newFilter}></TextBox>
                                        <div className={'text-secondary !k-font-size-sm mt-1'}>
                                            Button will only show under items that match the filter text
                                        </div>
                                    </div>
                                )}
                            </Reveal>
                        </div>
                    </div>
                    <div className={'d-flex justify-content-end gap-2 mt-3'}>
                        <Button
                            onClick={() => {
                                setShowNewDialog(false);
                                setEditItem(null);
                            }}>
                            Cancel
                        </Button>
                        <Button
                            themeColor={'primary'}
                            disabled={isNameDuplicate || newButtonName.value.trim() === '' || eliButtons.length >= MAX_ELI_BUTTONS}
                            onClick={() => {
                                const newButton: EliCustomButton = {
                                    label: newButtonName.value.trim(),
                                    urlTemplate: newUrlTemplate.value.trim(),
                                    filter: newFilterSectionOpen.value ? newFilter.value.trim() || null : null
                                };
                                const newButtons = [...eliButtons];
                                if (editItem) {
                                    const editIndex = newButtons.findIndex((b) => b === editItem);
                                    if (editIndex !== -1) {
                                        newButtons[editIndex] = newButton;
                                    }
                                } else {
                                    newButtons.push(newButton);
                                }
                                dispatch(
                                    setSetting(
                                        settingKey,
                                        newButtons.filter((b) => b !== undefined && b !== null)
                                    )
                                );

                                setShowNewDialog(false);
                                setEditItem(null);
                            }}>
                            {editItem ? 'Save' : 'Create'}
                        </Button>
                    </div>
                </Dialog>
            )}
            {showConfirmDelete !== null && (
                <Dialog title={'Confirm Delete'} onClose={() => setShowConfirmDelete(null)}>
                    <p>Are you sure you want to delete "{eliButtons[showConfirmDelete].label}"?</p>
                    <DialogActionsBar>
                        <Button onClick={() => setShowConfirmDelete(null)}>Cancel</Button>
                        <Button
                            themeColor={'error'}
                            onClick={() => {
                                const newButtons = eliButtons.filter((_, i) => i !== showConfirmDelete);
                                dispatch(setSetting(settingKey, newButtons));
                                setShowConfirmDelete(null);
                            }}>
                            Delete
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
}

function EliButton(props: {
    id: number;
    button: EliCustomButton;
    type: EliType;
    onEdit?: (button: EliCustomButton) => void;
    onDelete?: (index: number) => void;
}) {
    const { id, button } = props;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    const tokenRegex = new RegExp(`(#(?:${ELI_TOKENS[props.type].join('|')})#)`, 'gi');
    const urlWithHighlight = button.urlTemplate.split(tokenRegex).map((part, index) => {
        if (part && ELI_TOKENS[props.type].some((token) => part.toUpperCase() === `#${token}#`)) {
            return (
                <span key={index} className={'text-primary'}>
                    {part}
                </span>
            );
        } else {
            return <span key={index}>{part}</span>;
        }
    });

    return (
        <div ref={setNodeRef} style={style}>
            <div className={'d-flex justify-content-between align-items-center'}>
                <SvgIcon icon={DragOutline} {...attributes} {...listeners} svgStyle={{ outline: 'none', width:'16px' }} />
                <div className={'d-flex flex-column flex-grow-1 mx-2'} style={{ minWidth: 0 }}>
                    <div className={'d-flex align-items-center gap-1'}>
                        {button.label}
                        {button.filter !== null && (
                            <div className={'fe-badge-secondary'}>
                                <SvgIcon icon={FilterOutline} />
                                {button.filter}
                            </div>
                        )}
                    </div>
                    {button.urlTemplate.length > 0 ? (
                        <div
                            className={'text-success !k-font-size-sm'}
                            title={button.urlTemplate}
                            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                            {urlWithHighlight}
                        </div>
                    ) : (
                        <div className={'text-warning !k-font-size-sm'}>No URL template set</div>
                    )}
                </div>
                <div className={'d-flex gap-1'}>
                    <Button
                        size={'small'}
                        fillMode={'outline'}
                        svgIcon={EditOutline}
                        title={'Edit button'}
                        onClick={() => props.onEdit?.(button)}></Button>
                    <Button
                        size={'small'}
                        fillMode={'outline'}
                        svgIcon={TrashOutline}
                        title={'Delete button'}
                        onClick={() => props.onDelete?.(id)}
                        themeColor={'error'}></Button>
                </div>
            </div>
        </div>
    );
}
